import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { UpsertDigitalDossierBankingInformationDto } from '../../../../services/types/query-params';
import { DigitalDossierBankingInformationSerializer } from '@bcba/serializers';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import { VALIDATION_STATUSES, YES_NO_OPTIONS } from '@bcba/types';
import {
  QueryObserverResult,
  RefetchOptions,
  useMutation,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { updateBankingInformation } from '../../../../services/digital-dossiers/update-information';
import { useDigitalDossierSectionContext } from '../../context/DigitalDossierSectionContext';
import { useDigitalDossierActionButtons } from '../../hooks/useDigitalDossierActionButtons';
import { usePendingChangesAlert } from '../../../../contexts/hooks';

const resolver = classValidatorResolver(
  UpsertDigitalDossierBankingInformationDto,
);

const getDefaultAccountValue = (
  acc?: DigitalDossierBankingInformationSerializer,
) => {
  const { uuid, bankEntity, cbu, cbuDocument } = acc ?? {};
  return {
    uuid,
    bankEntity: bankEntity || '',
    cbu: cbu || '',
    cbuDocument: {
      uuid: cbuDocument?.uuid,
      keepOriginal: Boolean(cbuDocument),
    },
  };
};

export const useBankingInfoForm = (
  data: DigitalDossierBankingInformationSerializer[],
  refetch: (
    options?: RefetchOptions | undefined,
  ) => Promise<
    QueryObserverResult<DigitalDossierBankingInformationSerializer[], Error>
  >,
) => {
  const { t } = useTranslation();
  const { refetchDigitalDossierStatus } = useDigitalDossierSectionContext();

  const { showSuccessToast } = useToastsHook();

  // We are assuming that all elements of the array have the same status and validationMessage
  const status = data[0]?.status ?? VALIDATION_STATUSES.INCOMPLETE;
  const validationMessage = data[0]?.validationMessage;
  const isPendingChangeRequest =
    status === VALIDATION_STATUSES.PENDING_CHANGE_REQUEST;
  const defaultHasBankingInfo =
    data[0]?.hasBankingInfo === false ? YES_NO_OPTIONS.NO : YES_NO_OPTIONS.YES;

  const accounts = data.length
    ? data.map((acc) => getDefaultAccountValue(acc))
    : [getDefaultAccountValue()];

  const formMethods = useForm<UpsertDigitalDossierBankingInformationDto>({
    resolver,
    defaultValues: {
      hasBankingInfo: defaultHasBankingInfo,
      accounts,
    },
  });

  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { dirtyFields },
  } = formMethods;

  const isDirty = Boolean(Object.keys(dirtyFields).length);
  usePendingChangesAlert(isDirty);
  const { mutate, isPending: isSaveLoading } = useMutation({
    mutationFn: (data: UpsertDigitalDossierBankingInformationDto) =>
      updateBankingInformation(data),
    onSuccess: () => {
      refetchDigitalDossierStatus();
      showSuccessToast({
        title: t(
          'explorer.digitalDossier.updateBankingInfo.success.toast.title',
        ),
      });
      refetch();
    },
  });

  const { actionButtons, isFormReadOnly } = useDigitalDossierActionButtons({
    status,
    isLoadingSave: isSaveLoading,
    formHasChanges: isDirty,
  });

  const onSubmit: SubmitHandler<UpsertDigitalDossierBankingInformationDto> = (
    data,
  ) => {
    if (data.hasBankingInfo === YES_NO_OPTIONS.NO) delete data.accounts;
    mutate(data);
  };

  const {
    fields: accountsFields,
    append,
    remove,
  } = useFieldArray({
    name: 'accounts',
    control,
  });

  const addNewBankAccountHandler = () => append(getDefaultAccountValue());
  const removeBankAccountHandler = (index: number) => remove(index);

  const canUpdate =
    status === VALIDATION_STATUSES.INCOMPLETE ||
    status === VALIDATION_STATUSES.PENDING_CHANGE_REQUEST ||
    !isFormReadOnly;

  const hasBankingInfo = watch('hasBankingInfo') === YES_NO_OPTIONS.YES;

  // This is to reset form after submit
  useEffect(() => {
    reset(
      {
        hasBankingInfo: defaultHasBankingInfo,
        accounts,
      },
      { keepDirty: false },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return {
    formMethods,
    handleSubmit,
    onSubmit,
    hasBankingInfo,
    canUpdate,
    addNewBankAccountHandler,
    removeBankAccountHandler,
    status,
    isPendingChangeRequest,
    validationMessage,
    defaultHasBankingInfo,
    accountsFields,
    actionButtons,
  };
};
